// Skin analysis types for YouCam/PerfectCorp API results

// ===== BASIC TYPES (kept for backward compatibility) =====

export interface SkinMetrics {
  // Core metrics (teaser + full)
  pores: number        // 0-100, higher = more visible pores
  texture: number      // 0-100, higher = rougher texture
  pigmentation: number // 0-100, higher = more uneven pigmentation
  redness: number      // 0-100, higher = more redness
  wrinkles: number     // 0-100, higher = more wrinkles
  brightness: number   // 0-100, higher = brighter/more radiant (legacy, use radiance)
  oiliness: number     // 0-100, higher = more oily
  acne: number         // 0-100, higher = more acne
  // HD-only metrics
  moisture?: number    // 0-100, higher = more dehydrated (concern score)
  firmness?: number    // 0-100, higher = less firm (concern score)
  radiance?: number    // 0-100, higher = more dull (concern score)
  spots?: number       // 0-100, higher = more age spots (concern score)
  darkCircles?: number // 0-100, higher = more dark circles
  eyeBags?: number     // 0-100, higher = more eye bags
  droopyEyelidUpper?: number // 0-100, higher = more droopy
  droopyEyelidLower?: number // 0-100, higher = more droopy
}

// Zone-specific heatmaps for wrinkles (7 zones)
export interface WrinkleZoneHeatmaps {
  forehead?: string     // Horizontal forehead lines
  glabellar?: string    // Between eyebrows (frown lines)
  crowfeet?: string     // Outer eye corners
  periocular?: string   // Around eyes
  nasolabial?: string   // Nose to mouth folds
  marionette?: string   // Mouth to chin lines
  whole?: string        // Overall face
}

// Zone-specific heatmaps for pores (4 zones)
export interface PoreZoneHeatmaps {
  forehead?: string     // T-zone upper
  nose?: string         // T-zone center
  cheek?: string        // U-zone
  whole?: string        // Overall face
}

// Zone-specific scores for wrinkles (concern scores: 0-100, higher = worse)
export interface WrinkleZoneScores {
  forehead?: number
  glabellar?: number
  crowfeet?: number
  periocular?: number
  nasolabial?: number
  marionette?: number
}

// Zone-specific scores for pores (concern scores: 0-100, higher = worse)
export interface PoreZoneScores {
  forehead?: number
  nose?: number
  cheek?: number
}

export interface Heatmaps {
  // Core metrics (teaser + full)
  pores: string        // URL to heatmap image
  texture: string      // URL to heatmap image
  pigmentation: string // URL to heatmap image (age spots)
  wrinkles: string     // URL to heatmap image
  redness: string      // URL to heatmap image
  // HD-only metrics (full analysis)
  acne?: string
  oiliness?: string
  moisture?: string
  firmness?: string
  radiance?: string
  darkCircles?: string
  eyeBags?: string
  droopyEyelidUpper?: string
  droopyEyelidLower?: string
  // Zone-specific heatmaps (HD only)
  wrinkleZones?: WrinkleZoneHeatmaps
  poreZones?: PoreZoneHeatmaps
}

export type SkinType = 'dry' | 'normal' | 'combination' | 'oily'

export interface AnalysisResults {
  skinAge: number
  skinScore: number
  skinType: SkinType
  metrics: SkinMetrics
  heatmaps: Heatmaps
  photoUrl: string
  analyzedAt: string
  // Zone-specific scores (HD only)
  wrinkleZoneScores?: WrinkleZoneScores
  poreZoneScores?: PoreZoneScores
}

// ===== ENHANCED TYPES FOR $99 PREMIUM REPORT =====

// All 14 HD metrics from YouCam API
export type MetricKey =
  | 'pores'
  | 'wrinkles'
  | 'texture'
  | 'acne'
  | 'redness'
  | 'darkCircles'
  | 'eyeBags'
  | 'spots'
  | 'oiliness'
  | 'moisture'
  | 'firmness'
  | 'radiance'
  | 'droopyEyelidUpper'
  | 'droopyEyelidLower'

// Metric categories for grouping in UI
export type MetricCategory = 'skinHealth' | 'agingSigns' | 'eyeArea'

// Status based on score comparison with age average
export type MetricStatus = 'good' | 'normal' | 'attention'

// Zone-specific breakdown (for metrics that support it)
export interface ZoneScores {
  forehead?: number
  tZone?: number
  nose?: number
  cheeks?: number
  uZone?: number
  crowsFeet?: number
  glabella?: number
  nasolabial?: number
  marionette?: number
  periocular?: number
}

// Individual metric with full detail for deep-dive pages
export interface MetricDetail {
  key: MetricKey
  name: string                    // Display name (e.g., "Pores", "Dark Circles")
  category: MetricCategory        // For grouping in UI
  score: number                   // 0-100, user's score
  ageAverage: number              // Benchmark for user's age group
  status: MetricStatus            // Derived from score vs ageAverage
  zones?: ZoneScores              // Zone-specific scores (if applicable)
  heatmapUrl?: string             // URL to YouCam-provided heatmap PNG
  zoneHeatmaps?: Record<string, string>  // Zone-specific heatmap URLs
}

// All 14 metrics with detailed information
export interface EnhancedMetrics {
  pores: MetricDetail
  wrinkles: MetricDetail
  texture: MetricDetail
  acne: MetricDetail
  redness: MetricDetail
  darkCircles: MetricDetail
  eyeBags: MetricDetail
  spots: MetricDetail
  oiliness: MetricDetail
  moisture: MetricDetail
  firmness: MetricDetail
  radiance: MetricDetail
  droopyEyelidUpper: MetricDetail
  droopyEyelidLower: MetricDetail
}

// Enhanced analysis results for premium report
export interface EnhancedAnalysisResults {
  skinAge: number
  skinScore: number
  skinType: SkinType
  photoUrl: string
  metrics: EnhancedMetrics
  topConcerns: MetricKey[]        // Top 3 metric keys sorted by severity
  analyzedAt: string
}

// Metric info for UI display (from metricExplanations.ts)
export interface MetricInfo {
  key: MetricKey
  name: string
  category: MetricCategory
  icon: string                    // Emoji or icon identifier
  description: string             // Short description
  explanation: string             // Educational content
  ingredients: string[]           // Recommended ingredients
  hasZones: boolean               // Whether metric supports zone breakdown
  zoneLabels?: Record<string, string>  // Zone key -> display label
}

// Helper function to determine status from score vs age average
// Higher score = better skin condition
export function getMetricStatus(score: number, ageAverage: number): MetricStatus {
  const diff = score - ageAverage
  if (diff >= 10) return 'good'       // Better than average (higher score)
  if (diff >= -10) return 'normal'    // Within normal range
  return 'attention'                   // Needs attention (lower score)
}

// Helper function to get status color
export function getStatusColor(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return '#22C55E' // green
    case 'normal':
      return '#F59E0B' // amber
    case 'attention':
      return '#EF4444' // red
  }
}

// Helper function to get status label
export function getStatusLabel(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return 'Good'
    case 'normal':
      return 'Normal'
    case 'attention':
      return 'Needs Attention'
  }
}

export type SeverityLevel = 'mild' | 'moderate' | 'severe'

// Higher score = better condition, so high score = mild (good), low score = severe (bad)
export function getSeverityLevel(score: number): SeverityLevel {
  if (score >= 70) return 'mild'      // Good condition
  if (score >= 40) return 'moderate'  // Moderate condition
  return 'severe'                      // Needs attention
}

export function getSeverityColor(level: SeverityLevel): string {
  switch (level) {
    case 'mild':
      return '#22C55E' // green
    case 'moderate':
      return '#F59E0B' // amber
    case 'severe':
      return '#EF4444' // red
  }
}

// Calculate skin score based on PRD formula
export function calculateSkinScore(metrics: SkinMetrics): number {
  const score =
    (100 - metrics.pores) * 0.30 +
    (100 - metrics.texture) * 0.25 +
    (100 - metrics.pigmentation) * 0.20 +
    (100 - metrics.redness) * 0.15 +
    (100 - metrics.wrinkles) * 0.10

  return Math.round(score)
}

// Top concerns for the analysis
export interface SkinConcern {
  id: string
  name: string
  score: number
  severity: SeverityLevel
  description: string
}

export function getTopConcerns(metrics: SkinMetrics, limit: number = 3): SkinConcern[] {
  const concerns: SkinConcern[] = [
    {
      id: 'pores',
      name: 'Pores',
      score: metrics.pores,
      severity: getSeverityLevel(metrics.pores),
      description: 'Enlarged or visible pores',
    },
    {
      id: 'texture',
      name: 'Texture',
      score: metrics.texture,
      severity: getSeverityLevel(metrics.texture),
      description: 'Skin surface roughness',
    },
    {
      id: 'pigmentation',
      name: 'Pigmentation',
      score: metrics.pigmentation,
      severity: getSeverityLevel(metrics.pigmentation),
      description: 'Uneven skin tone or dark spots',
    },
    {
      id: 'redness',
      name: 'Redness',
      score: metrics.redness,
      severity: getSeverityLevel(metrics.redness),
      description: 'Skin redness or irritation',
    },
    {
      id: 'wrinkles',
      name: 'Wrinkles',
      score: metrics.wrinkles,
      severity: getSeverityLevel(metrics.wrinkles),
      description: 'Fine lines and wrinkles',
    },
    {
      id: 'acne',
      name: 'Acne',
      score: metrics.acne,
      severity: getSeverityLevel(metrics.acne),
      description: 'Active acne or blemishes',
    },
  ]

  // Sort by score ascending (lowest = worst) and take top N
  return concerns
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
}
